import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { organizationService } from '../services/organizationService';
import { outletService } from '../services/outletService';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import { useAppData } from '../context/AppDataContext';
import { toast } from '../utils/toast';
import Combobox from './Combobox';
import './QuickCreate.css';

/** Small modal used by the "+ Create …" options of the pickers. */
function QuickModal({ title, subtitle, onClose, onSubmit, saving, submitLabel, children }) {
  useEffect(() => {
    const onKey = (event) => event.key === 'Escape' && !saving && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);
  // Rendered at the end of <body>: the pickers often sit inside another form.
  return createPortal(
    <div className="qc" role="dialog" aria-modal="true" aria-labelledby="qc-title">
      <div className="qc__backdrop" onClick={() => !saving && onClose()} />
      <form
        className="qc__panel"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onSubmit();
        }}
      >
        <header className="qc__head">
          <div>
            <h3 id="qc-title">{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" className="qc__close" onClick={onClose} disabled={saving} aria-label="Close">
            ×
          </button>
        </header>
        <div className="qc__body">{children}</div>
        <footer className="qc__foot">
          <button type="button" className="qc-btn qc-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="qc-btn qc-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : submitLabel}
          </button>
        </footer>
      </form>
    </div>,
    document.body,
  );
}

/** Adds the new record to the cached list so every open page sees it at once. */
function useAddToCache() {
  const { updateResource, invalidateResource } = useAppData();
  return (key, record) => {
    updateResource(key, (list) => [...(Array.isArray(list) ? list : []), record]);
    invalidateResource(key);
  };
}

/** Quick "new client (organization)": name, brand code, contact. */
export function QuickClientForm({ initialName = '', onClose, onCreated }) {
  const addToCache = useAddToCache();
  const [form, setForm] = useState({ name: initialName, brand_code: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Client not added', 'Enter the client name.');
      return;
    }
    if (form.brand_code && !/^[A-Za-z]{2}$/.test(form.brand_code.trim())) {
      toast.error('Client not added', 'The brand code must be two letters, e.g. PT.');
      return;
    }
    setSaving(true);
    try {
      const org = await organizationService.create({
        name: form.name.trim(),
        brand_code: form.brand_code.trim().toUpperCase() || undefined,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      });
      addToCache('organizations', org);
      toast.success('Client added', org.name);
      onCreated(org);
    } catch (err) {
      toast.error('Client not added', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickModal title="New client" subtitle="Add the rest of the details later on Clients & Stores." onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Add client">
      <label>
        Client name *
        <input value={form.name} onChange={set('name')} autoFocus required maxLength={255} placeholder="e.g. Pantaloons" />
      </label>
      <div className="qc__grid">
        <label>
          Brand code
          <input value={form.brand_code} onChange={set('brand_code')} maxLength={2} placeholder="PT" />
          <small>Two letters, used in schedule numbers. Blank = from the name.</small>
        </label>
        <label>
          Phone
          <input value={form.phone} onChange={set('phone')} maxLength={20} />
        </label>
      </div>
      <label>
        Email
        <input type="email" value={form.email} onChange={set('email')} maxLength={255} />
      </label>
    </QuickModal>
  );
}

/** Quick "new store (outlet)" of a client. */
export function QuickStoreForm({ organizations, organizationId = '', initialName = '', onClose, onCreated }) {
  const addToCache = useAddToCache();
  const [form, setForm] = useState({ organization_id: organizationId ? String(organizationId) : '', name: initialName, outlet_code: '', city: '', state: '' });
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const orgOptions = useMemo(
    () => organizations.map((o) => ({ value: String(o.id), label: o.name, sub: o.brand_code || '' })),
    [organizations],
  );

  const submit = async () => {
    if (!form.organization_id) {
      toast.error('Store not added', 'Choose the client this store belongs to.');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Store not added', 'Enter the store name.');
      return;
    }
    setSaving(true);
    try {
      const outlet = await outletService.create({
        organization_id: Number(form.organization_id),
        name: form.name.trim(),
        outlet_code: form.outlet_code.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
      });
      const org = organizations.find((o) => String(o.id) === String(form.organization_id));
      const record = { ...outlet, organization_name: outlet.organization_name || org?.name };
      addToCache('outlets', record);
      toast.success('Store added', `${record.name}${org ? ` · ${org.name}` : ''}`);
      onCreated(record);
    } catch (err) {
      toast.error('Store not added', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickModal title="New store" subtitle="Address, contact and auditors can be added later on Clients & Stores." onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Add store">
      <label>
        Client *
        <Combobox options={orgOptions} value={form.organization_id} onChange={(v) => setForm((p) => ({ ...p, organization_id: v }))} placeholder="Type the client name…" required ariaLabel="Client" />
      </label>
      <div className="qc__grid">
        <label>
          Store name *
          <input value={form.name} onChange={set('name')} autoFocus={Boolean(form.organization_id)} required maxLength={255} placeholder="e.g. PT Thane Store" />
        </label>
        <label>
          Store code
          <input value={form.outlet_code} onChange={set('outlet_code')} maxLength={50} placeholder="e.g. P001" />
        </label>
        <label>
          City
          <input value={form.city} onChange={set('city')} maxLength={100} />
        </label>
        <label>
          State
          <input value={form.state} onChange={set('state')} maxLength={100} />
        </label>
      </div>
    </QuickModal>
  );
}

/** Quick "new client login" (portal access, read-only) for a client / store. */
export function QuickClientLoginForm({ organizations, outlets, organizationId = '', outletId = '', onClose, onCreated }) {
  const addToCache = useAddToCache();
  const [roleId, setRoleId] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    client_title: '',
    phone: '',
    organization_id: organizationId ? String(organizationId) : '',
    access: outletId ? 'outlet' : 'organization',
  });
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  useEffect(() => {
    roleService
      .getAll()
      .then((roles) => setRoleId(String((roles || []).find((r) => r.name === 'client')?.id || '')))
      .catch(() => setRoleId(''));
  }, []);

  const outlet = outlets.find((o) => String(o.id) === String(outletId));
  const orgOptions = useMemo(() => organizations.map((o) => ({ value: String(o.id), label: o.name })), [organizations]);

  const submit = async () => {
    if (!form.email.trim() || !form.organization_id) {
      toast.error('Login not added', 'Enter the email and choose the client.');
      return;
    }
    if (!roleId) {
      toast.error('Login not added', 'The client role was not found.');
      return;
    }
    setSaving(true);
    try {
      const forStore = form.access === 'outlet' && outlet;
      const user = await userService.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        client_title: form.client_title.trim(),
        role_id: Number(roleId),
        provider: 'google',
        client_organization_id: Number(form.organization_id),
        client_access_level: forStore ? 'outlet' : 'organization',
        client_outlet_ids: forStore ? [Number(outlet.id)] : [],
      });
      addToCache('users', {
        ...user,
        role_name: user.role_name || 'client',
        client_organization_id: Number(form.organization_id),
        client_access_level: forStore ? 'outlet' : 'organization',
        client_outlets: forStore ? [{ id: outlet.id, name: outlet.name, outlet_code: outlet.outlet_code }] : [],
      });
      toast.success('Client login added', `${form.email.trim()} can now view ${forStore ? outlet.name : 'the client’s schedules'}.`);
      onCreated(user);
    } catch (err) {
      toast.error('Login not added', err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickModal title="New client login" subtitle="They sign in with Google and see this client’s schedule portal, read-only." onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Add login">
      <div className="qc__grid">
        <label>
          Name
          <input value={form.name} onChange={set('name')} autoFocus maxLength={255} />
        </label>
        <label>
          Email *
          <input type="email" value={form.email} onChange={set('email')} required maxLength={255} placeholder="name@company.com" />
        </label>
        <label>
          Title
          <input value={form.client_title} onChange={set('client_title')} maxLength={100} placeholder="e.g. Store Manager" />
        </label>
        <label>
          Phone
          <input value={form.phone} onChange={set('phone')} maxLength={20} />
        </label>
      </div>
      <label>
        Client *
        <Combobox options={orgOptions} value={form.organization_id} onChange={(v) => setForm((p) => ({ ...p, organization_id: v }))} required ariaLabel="Client" />
      </label>
      <fieldset className="qc__choices">
        <legend>Can view</legend>
        {outlet ? (
          <label>
            <input type="radio" name="qc-access" checked={form.access === 'outlet'} onChange={() => setForm((p) => ({ ...p, access: 'outlet' }))} />
            Only this store ({outlet.name})
          </label>
        ) : null}
        <label>
          <input type="radio" name="qc-access" checked={form.access === 'organization'} onChange={() => setForm((p) => ({ ...p, access: 'organization' }))} />
          Every store of the client
        </label>
      </fieldset>
    </QuickModal>
  );
}
